import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RefreshCw, StickyNote, Check } from 'lucide-react';
import { getErrorMessage } from '../services/api';

const API_URL = BACKEND_URL;

/**
 * Prompts the user to add a Bullhorn note against the associated record
 * after a document is sent. Skippable.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void              fires on Skip OR after successful save
 *   - entityType: 'candidate' | 'contact' | 'placement'
 *   - entityId:   Bullhorn ID of the record to note against
 *   - entityName: display name ("John Smith")  — rendered in the prompt
 *   - documentId: (optional) internal document id, logged for audit trail
 *   - defaultAction: (optional) preferred note action, defaults to 'General'
 *   - defaultComments: (optional) pre-populated note body
 *   - apiClient:  (optional) custom axios instance — pass `embedApi` from the
 *                 Bullhorn iframe so the request carries the embed bearer
 *                 token. Defaults to the cookie-authed global axios.
 */
export default function BullhornNotePromptDialog({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  documentId,
  defaultAction = 'General',
  defaultComments = '',
  apiClient,
}) {
  const client = apiClient || axios;
  // The embed iframe uses a relative-baseURL axios instance (`/api/...`).
  // The main-app (cookie auth) uses absolute REACT_APP_BACKEND_URL.
  // Prefer relative paths when an explicit apiClient was passed.
  const url = (path) => (apiClient ? path : `${API_URL}${path}`);

  const [actions, setActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState(defaultAction);
  const [comments, setComments] = useState(defaultComments);
  const [saving, setSaving] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);

  // Load valid note actions from Bullhorn once the dialog opens
  useEffect(() => {
    if (!open) return;
    setComments(defaultComments);
    setSelectedAction(defaultAction);
    setLoadingActions(true);
    client
      .get(url('/api/bullhorn/note-actions'))
      .then((res) => {
        const opts = res.data?.actions || [];
        setActions(opts);
        // Pick the first option that matches defaultAction, else first option
        const match = opts.find((o) => o.value === defaultAction);
        setSelectedAction(match ? match.value : opts[0]?.value || 'General');
      })
      .catch((err) => {
        console.warn('note-actions fetch failed', err?.response?.status, err?.message);
      })
      .finally(() => setLoadingActions(false));
    // `client` and `url` are stable for a given mount of the dialog; we
    // intentionally only refire on open/defaultAction/defaultComments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultAction, defaultComments]);

  const handleSave = async () => {
    if (!comments.trim()) {
      toast.error('Please enter some note content');
      return;
    }
    setSaving(true);
    try {
      await client.post(url('/api/bullhorn/notes'), {
        entity_type: entityType,
        entity_id: entityId,
        action: selectedAction,
        comments: comments.trim(),
        document_id: documentId || null,
      });
      toast.success(`Note added to ${entityName || entityType} in Bullhorn`);
      onClose?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save note'));
    } finally {
      setSaving(false);
    }
  };

  const entityLabel =
    entityType === 'placement' ? 'placement'
      : entityType === 'contact' ? 'contact record'
        : 'candidate record';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="bullhorn-note-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-[#1a4a5e]" />
            Add a note to Bullhorn?
          </DialogTitle>
          <DialogDescription>
            Log this send against {entityName ? (
              <>the {entityLabel} for <strong>{entityName}</strong></>
            ) : (
              <>the {entityLabel}</>
            )}. The note will appear on the Notes tab in Bullhorn.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="note-action">Note type</Label>
            <Select value={selectedAction} onValueChange={setSelectedAction} disabled={loadingActions}>
              <SelectTrigger id="note-action" data-testid="note-action-select">
                <SelectValue placeholder={loadingActions ? 'Loading…' : 'Select note type'} />
              </SelectTrigger>
              <SelectContent>
                {actions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note-content">Note content</Label>
            <Textarea
              id="note-content"
              data-testid="note-content-input"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="What should we record against this record?"
              rows={5}
            />
            <p className="text-xs text-muted-foreground">
              Tip: include template name, sender, or any context you want to keep for future reference.
            </p>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onClose?.()}
            disabled={saving}
            data-testid="note-skip-button"
          >
            Skip
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !comments.trim()}
            data-testid="note-save-button"
          >
            {saving ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving…</>
            ) : (
              <><Check className="w-4 h-4 mr-2" />Save Note</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
